import Big from 'big.js'
import {
  MissionsConditions,
  SupportedToken,
} from '../../modules/missionControl/slice'
import { LithiumAccountSlice } from '../../modules/user/slice'
import { PoolSettings } from '../../types/reducerTypes'

export type TwitterTask = {
  taskType: string
  accountHandleOrTweetUrl: string
}

export type SubtaskSubmission = {
  hasCompleted: boolean
  score: number
  published_at: string
  subtaskId: number
  shouldTriggerAnimation?: boolean
}

export type AccountStatus = {
  hasEmail: boolean
  hasDiscord: boolean
  hasTwitter: boolean
  hasTelegram: boolean
}

export type TasksLength = {
  active: number
  pending: number
  complete: number
  expired: number
  all: number
}

export interface ButtonProps {
  id: number
  submissionPublishedAt?: string
  isPending?: boolean
}

export type SubmissionsByTask = {
  [key: string]: {
    submissionId: number
    hasCompleted: boolean
    score: number
    published_at?: string
    subtasks?: SubtaskSubmission[]
    createdAt: string
    completionsCount?: number
    periodStartDate?: Date
  }[]
}
export type TangibleReward =
  | 'extraAllocation'
  | 'USDC'
  | 'nativeToken'
  | 'customToken'
  | 'lithiumShopCoin'

export type ReferralSettings = {
  customTokenRewardAddress?: string
  customTokenTicker?: string
  tangibleRewardAmount?: number
  xpRewardAmount?: number
  tangibleRewardType: TangibleReward
}

export type ReferralScheme = {
  referrerReward: ReferralSettings
  refereeReward: ReferralSettings
  totalRewardsPool: number
}

export type TelegramTask = {
  taskType: string
  url: string
}

export type DiscordTask = {
  taskType: string
  channelId: string
  inviteLink: string
}

export const ReferralVerification = {
  WALLET_CONNECT: 'wallet_connect_verification',
  COMPLETE_MISSION: 'complete_mission_verification',
  COMPLETE_MISSION_AND_EMAIL: 'complete_mission_and_email_verification',
} as const

export const referralVerificationValues = Object.values(ReferralVerification)

export type ReferralVerificationUnion =
  typeof ReferralVerification[keyof typeof ReferralVerification]

export enum NewTaskType {
  TWITTER_VIEW = 'twitter_views',
  ON_CHAIN = 'on_chain',
  MANUAL_API_REWARD = 'manual_api_reward',
  GRAPH_INDEXER = 'graph_indexer',
  VERIFY_WALLET = 'verify_wallet',
  ZAP_VALUE_INDEX = 'zap_value_index',
  ZAP_LAUNCH = 'zap_launch',
}

export enum ZapLaunchSubType {
  INVEST_IN_POOL = 'invest_in_pool',
  CREATE_A_POOL = 'create_a_pool',
  HAVE_A_POOL_GRADUATE = 'have_a_pool_graduate',
}

export type ZapLaunchMetaData = {
  type: ZapLaunchSubType
  quantity?: number
}

export type TwitterViewsMetaData = {
  clientHandle: string
  tweetAutofill?: string
}

export type VerifyWalletMetaData = {
  walletType: string
  wallets: string[]
}

export type ManualApiRewardMetaData = {
  redirectLink?: string
}

export type TaskMetaData =
  | (TwitterViewsMetaData & VariableRewardMetaData)
  | VariableRewardMetaData
  | VerifyWalletMetaData
  | ZapLaunchMetaData
  | ManualApiRewardMetaData

export type SubTask = {
  id: number
  publicName: string
  instructions: string
  noScreenshotRequired: boolean
  requiresSocial?: 'None' | 'Twitter' | 'Discord' | 'Telegram'
  twitterTaskType?: 'None' | 'Retweet' | 'Follow' | 'Like'
  accountHandleOrTweetUrl?: string
  telegramUrl?: string
  discordServerId?: string
  discordInviteLink?: string
  isEmailTask?: boolean
  isReferralTask?: boolean
  completionMethod: 'binary' | 'oneToTenScore'
  visitUrl?: string
  formMissionId?: string
  onChainTask?: {
    id: string
    type: string
  }
  newTaskType?: NewTaskType
  isRollupMission?: boolean
  metaData?: TaskMetaData
  imageVerification?:
    | 'youtube_verification'
    | 'spotify_verification'
    | 'tiktok_verification'
    | 'instagram_verification'
  referralVerification?: ReferralVerificationUnion
}

export type VariableRewardMetaData =
  | LinearVariableRewardMetaData
  | TierVariableRewardMetaData

export type LinearVariableRewardMetaData = {
  rewardDistributionType: 'linear'
  metric: number
  rewardPerMetric: number
  metricName: string
  maxReward: number
}

export type TierVariableRewardMetaData = {
  rewardDistributionType: 'tiers'
  tiers: Tier[]
  metricName: string
}
type Tier = [TierReward, TierMetric, TierMaximum]
type TierReward = number
type TierMetric = number
type TierMaximum = number

export type Media = {
  topBanner: {
    lightTheme: MediaItem
    darkTheme: MediaItem
  }
  projectThumbnail: MediaItem
  featuredProjectImage: MediaItem
  highlightsVideo: MediaItem
  mainHomeImage: MediaItem
}

export enum CompetitionPrizeType {
  ERC20 = 'erc20',
  ERC721 = 'erc721',
  CUSTOM_SUCCESS_MESSAGE = 'custom_success_message',
  CODE = 'code',
  LINK = 'link',
}

export type CompetitionWinner = {
  userId: number
  username: string
  hasClaimed: boolean
}

export interface CompetitionPrize {
  id: string
  competition?: string
  name: string
  description: string
  quantity: number
  image: string
  type: CompetitionPrizeType
  chainId: number
  winnerPosition: number
  nftTokenIds?: Array<string>
  ownerAddress?: string
  nftContractAddress?: string
  nftMetadata?: Object
  competitionPrizeAddress?: string
  token?: number
  token_info: SupportedToken
  customSuccessMessage?: string
  link?: string
  code?: string
}

export type WonPrize = CompetitionPrize & {
  entry: {
    id: string
    competition: string
    user: number
    winnerPosition: number
    hasClaimed: boolean
    claimTxHash: string
    createdAt: string
    updatedAt: string
  }
}

export type CompetitionWinnerMap = {
  [key: number]: CompetitionWinner[]
}

export type WinningEntry = {
  id: string
  winnerPosition: number
  hasClaimed: true
  user_info: {
    id: 802
    username: string
    twitterHandle: string
    discordHandle: string
    telegramHandle: string
  }
}

export interface Competition {
  id?: string
  name: string
  description: string
  slug: string
  price: number
  maxEntries: number
  maxPerUser: number
  numberOfWinners: number
  missionConditions: MissionsConditions
  requiredLevel: number
  winnersSelected: boolean
  image: string
  isFeaturedCompetition: boolean
  isRaffle: boolean
  project: number
  startDate: Date
  endDate: Date
  updatedAt?: Date
  publishedOn: Date
  prizes: CompetitionPrize[]
  userPurchaseCount?: number
  entriesCount?: number
  allEntries?: { id: string }[]
  entries?: WinningEntry[]
  winners?: CompetitionWinnerMap
  userFailedClaimAt?: Date
}

type MediaItem = null | {
  url: string
}

export type MissionStatus = 'active' | 'pending' | 'complete' | 'expired'

export type Task = {
  id: number
  internalName?: string
  publicName: string
  pointReward: number
  multiplierReward?: number
  completionMethod: string | null
  ticker: string | null
  instructions: string
  requiresSocial: boolean | null
  noScreenshotRequired?: boolean | null
  expiresOn: string | null
  competition: string | null
  availableFrom: string | null
  thirdPartyProjectName: string | null
  isEmailTask: boolean | null
  xpReward: number | null
  requiredLevel: number | null
  editedAt: string
  publishedOn: string
  showLiveCounter: boolean
  order: number
  subtasks: SubTask[]
  status?: MissionStatus
  published_on?: string
  updated_at?: string
  available_from?: string
  completionsPerPeriod?: number
  frequency?: string
  isRollupMission?: boolean
  isReferralTask?: boolean
}

export interface UploadableImage {
  url: string
  width: number
  height: number
}

type ImageWithTheme = {
  lightTheme?: UploadableImage
  darkTheme?: UploadableImage
  url?: string
}

export interface EmbeddedMedia {
  topBanner?: ImageWithTheme
  featuredProjectImage?: ImageWithTheme
  projectThumbnail?: ImageWithTheme
  mainHomeImage?: ImageWithTheme
  pointsLogo?: ImageWithTheme
}

enum HeaderTheme {
  EL_CLASSICO = 'el_classico',
  FRESH_AND_CLEAN = 'fresh_and_clean',
  BIG_BOLD = 'big_bold',
}

export const headerThemes = {
  [HeaderTheme.EL_CLASSICO]: 'El Classico',
  [HeaderTheme.FRESH_AND_CLEAN]: "Fresh 'n' Clean",
  [HeaderTheme.BIG_BOLD]: "Big 'n' Bold",
}

export type Project = {
  id: number
  name: string
  maxAllocation: number
  ticker: string
  walletAddressesJSON: string[]
  slug: string
  metaDescription: string
  pool?: any
  whitelists: any
  published_at: string | null
  baselineAllocation?: number
  isFeaturedProject?: boolean
  tasks: Task[]
  excerpt?: string
  customDiscountInfo: {
    customTitle: string
    customDescription: string
    customPrice: number
  }
  competitions?: []
  projectMedia?: Media
  socials?: { [key: string]: string }
  letItSnow: boolean
  isComingSoon: boolean
  referralScheme?: ReferralScheme
  pointsName?: string
  embeddedMedia?: EmbeddedMedia
  headerTheme?: HeaderTheme
  coreColor?: string
  isDarkTheme?: boolean
  isMonochrome?: boolean
  font?: string
}

export type SerialisedUser = {
  displayName: string
  weeklySubmissions: number
  monthlySubmissions: number
  totalSubmissions: number
  totalXp: number
  weeklyGainInXp: number
}

export type UserBasePair = {
  account: number | string
  XP: number
}

export type WeeklyUser = UserBasePair & {
  weeklySubmissions: number
}

export type MonthlyUser = UserBasePair & {
  monthlySubmissions: number
}

export type AllTimeUser = UserBasePair & {
  totalSubmissions: number
}

export type LeaderboardMeta = {
  uniqueAllTimeUserCount: number
  uniqueThirtyDayUserCount: number
  uniqueWeeklyUserCount: number
}

export type SerialisedUsers = { [key: number | string]: SerialisedUser }

export type LeaderboardRow = {
  rank: number
  account: string
  totalXp: number
  level: number
}
export type Leaderboard = {
  rows: LeaderboardRow[]
  total: number
}
export type UserSlicedLeaderboard = {
  rows: LeaderboardRow[]
}

export type ReqBody = {
  taskId: number
  subtaskId?: number
  isSubtask?: boolean
  accountIdOfUser: number
  project: number
}

export type Sold = {
  total: Big
  private: Big
  public: Big
}

export type AccountError = {
  isWarning: boolean
  message: string
  newUser: LithiumAccountSlice
}

export type TierReference = {
  [key: string]: {
    [key: string]: {
      allocation: string
      required: string
    }
  }
}

export enum PoolStatusType {
  INCOMING = 'INCOMING',
  REVIEWING = 'REVIEWING',
  DENY = 'DENY',
  SCHEDULED = 'SCHEDULED',
  COMPLETED = 'COMPLETED',
}

export type EscrowMilestoneType = {
  milestone: number
  return: number
}

export type SaleType = {
  price: Big
  start: string
  end: string
  cap: number | Array<number>
}

export type TitledLinkType = {
  title: string
  link: string
}

export type ProjectAboutType = {
  logo: {
    alternativeText: string
    caption: string
    created_at: string
    ext: string
    formats: string | null
    hash: string
    height: number
    id: number
    mime: string
    name: string
    previewUrl: string | null
    provider: string
    provider_metadata: string | null
    size: number
    updated_at: string
    url: string
    width: number
  }
  highlightsText?: string
  webinar: string
  description: string
  links: Array<TitledLinkType>
  publishedAt: string
  createdAt: string
  updatedAt: string
  productText?: string
  problemText?: string
  solutionText?: string
  businessModalText?: string
  marketText?: string
  investorsText?: string
  tokenUtility?: string
  socialLinks?: string | undefined
}

export type TeamMemberType = {
  name: string
  position: string
  links: Array<TitledLinkType>
  avatar: null | string
}

export type TopLinks = {
  [key: string]: string
}

export interface VestingPoints {
  timestamp: string
  pct: string
}

export interface IPool {
  publicBuyLimit?: Big
  airDrop: Big
  escrowBlock: any
  totalSaleSupply: Big
  thresholdPublicAmount: Big
  token: string
  createToken: boolean
  name: string
  initialAddress: string
  poolAddress: string
  ticker: string
  totalSupply?: Big
  sold: Sold
  decimals: string
  escrow: number
  publicThreshold: Big
  escrowMilestones: Array<EscrowMilestoneType>
  status: PoolStatusType
  privateSale: SaleType
  publicSale: SaleType
  blacklist?: Array<string>
  about: ProjectAboutType
  team: Array<TeamMemberType>
  isPoolSoldOut?: boolean
  vestingPoints: VestingPoints[]
  poolContract?: any
  isDeployed?: boolean
  mainNet?: boolean
  isOldPool?: boolean
  chainID?: string
  topBannerDark?: { url: string }
  topBannerLight?: { url: string }
  crossChainLink?: string
  media?: Media
  listingType?: string
  vestingTableRow?: {
    percent: number
    label: string
  }[]
  isFeaturedProject?: boolean
  claimLocation?: string
  topLinks?: TopLinks
  athSinceIDO?: number
  isBusd?: boolean
  isLegacyPool?: boolean
  isTBC?: boolean
  progress?: number
  tgePrice?: number
  poolSettings?: PoolSettings
  whitelist?: any
  slug?: string
  hasCollectedUsdb?: boolean
  hasAddedLiquidity?: boolean
  isVetted?: boolean
}
export type RewardsInfo = {
  [key: string]: number
}

export type CompetitionReward = {
  amount: number
  rewardsDistributed: boolean
  competitionId: number
}

export type XPData = {
  currentLevelCeiling: number
  currentLevelFloor: number
  level: number
  totalXp: number
  totalRollupXp: number
  totalPoints: number
  totalMissionsXp?: number
  pendingNonDropMissionsCount?: number
  chapterRolloverXp?: number
}

export type TableRow = {
  user: SerialisedUser
  XP: number
  submissionsCount: number
  userId: number | string
}
